import React from 'react';
import { ArrowLeft, Shield, FileText, Scale, Lock, Users, Globe, AlertTriangle, Mail, CheckCircle, Clock, BookOpen } from 'lucide-react';

const TermsOfService = () => {
  const handleBackToHome = () => {
    // Replace with your React routing method
    // For React Router: navigate('/');
    // For Next.js: router.push('/');
    window.location.href = '/';
  };

  const handleContactClick = () => {
    // Replace with your React routing method
    // For React Router: navigate('/contact');
    // For Next.js: router.push('/contact');
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button 
            onClick={handleBackToHome}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-all duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-5xl font-light text-gray-900 mb-4">Terms of Service</h1>
            <div className="flex items-center justify-center text-gray-500 mb-8">
              <Clock className="w-4 h-4 mr-2" />
              <span>Last Updated: June 29, 2025</span>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Welcome to <strong>Manifest Illusions</strong>. By accessing our website or purchasing any of our digital products or services, you agree to the following Terms of Service. Please read them carefully.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        
        {/* Quick Navigation */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
          <div className="flex items-center mb-6">
            <BookOpen className="w-5 h-5 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Quick Navigation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'Overview', icon: <FileText className="w-4 h-4" />, href: '#overview' },
              { title: 'Acceptance', icon: <CheckCircle className="w-4 h-4" />, href: '#acceptance' },
              { title: 'Service Use', icon: <Users className="w-4 h-4" />, href: '#services' },
              { title: 'Payments', icon: <Scale className="w-4 h-4" />, href: '#payments' },
              { title: 'Delivery', icon: <Mail className="w-4 h-4" />, href: '#delivery' },
              { title: 'Refunds', icon: <AlertTriangle className="w-4 h-4" />, href: '#refunds' },
              { title: 'IP Rights', icon: <Lock className="w-4 h-4" />, href: '#ip' },
              { title: 'Privacy', icon: <Shield className="w-4 h-4" />, href: '#privacy' },
              { title: 'Liability', icon: <AlertTriangle className="w-4 h-4" />, href: '#liability' },
            ].map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="flex items-center p-3 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
              >
                <span className="text-blue-600 mr-3">{item.icon}</span>
                <span className="font-medium">{item.title}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Terms Content */}
        <div className="space-y-12">
          
          {/* Section 1: Overview */}
          <section id="overview" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">1. Overview</h2>
            </div>
            <div className="ml-16">
              <p className="text-lg text-gray-700 leading-relaxed">
                Manifest Illusions provides digital design services, creative assets, and conversion-optimized brand materials. All content on this site and all deliverables are protected under applicable copyright and trademark law.
              </p>
            </div>
          </section>

          {/* Section 2: Acceptance */}
          <section id="acceptance" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">2. Acceptance of Terms</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                By accessing our website, purchasing our services, or using any part of our platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                If you do not agree with any part of these terms, you must not use our services.
              </p>
            </div>
          </section>

          {/* Section 3: Use of Services */}
          <section id="services" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">3. Use of Our Services</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                You agree to use our services in accordance with applicable laws and not for any unlawful purpose. You may not resell or redistribute our digital products unless explicitly licensed.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="font-semibold text-red-900 mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  Prohibited Uses
                </h4>
                <ul className="text-red-800 space-y-2">
                  <li>• Violating any applicable laws or regulations</li>
                  <li>• Infringing on intellectual property rights</li>
                  <li>• Distributing malicious software or content</li>
                  <li>• Attempting to gain unauthorized access to our systems</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: Purchases and Payments */}
          <section id="payments" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mr-4">
                <Scale className="w-6 h-6 text-yellow-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">4. Purchases and Payments</h2>
            </div>
            <div className="ml-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <p className="text-gray-700">All purchases are processed securely via Stripe and PayPal.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <p className="text-gray-700">You agree to pay the listed price at checkout.</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <p className="text-gray-700">Taxes, if applicable, will be calculated at checkout.</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <p className="text-gray-700">Payment must be received in full before service delivery begins.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Delivery */}
          <section id="delivery" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mr-4">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">5. Delivery</h2>
            </div>
            <div className="ml-16">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">Digital products are delivered via download or email link within 1–5 business days, depending on the service.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">You must provide a valid email address and accurate information during checkout.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">Delivery times may vary during peak seasons or for complex custom projects.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Refunds and Revisions */}
          <section id="refunds" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">6. Refunds and Revisions</h2>
            </div>
            <div className="ml-16 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">Due to the custom and digital nature of our services, all sales are final.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">Revisions may be included depending on the service purchased.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">If you experience any issues, please contact support and we will make a reasonable effort to resolve them.</p>
                </div>
              </div>
              <div className="pt-4">
                <button 
                  onClick={handleContactClick}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </button>
              </div>
            </div>
          </section>

          {/* Section 7: Intellectual Property */}
          <section id="ip" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <Lock className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">7. Intellectual Property</h2>
            </div>
            <div className="ml-16 space-y-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">We retain ownership of all content, designs, and code until full payment is received.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">Upon purchase and delivery, you are granted a non-exclusive, royalty-free license to use your custom assets for personal or commercial use.</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                  <p className="text-gray-700">You may not copy, replicate, or resell our original content, frameworks, or templates without permission.</p>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <p className="text-amber-800 flex items-start">
                  <AlertTriangle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Important:</strong> Unauthorized use or distribution of our intellectual property may result in legal action and immediate termination of services.</span>
                </p>
              </div>
            </div>
          </section>

          {/* Section 8: AI & Automation */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center mr-4">
                <Globe className="w-6 h-6 text-cyan-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">8. AI & Automation Disclaimer</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Some services may include AI-generated components. All outputs are reviewed and refined by a human before delivery. By purchasing, you acknowledge and accept the use of AI-enhanced tools.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <p className="text-blue-800">
                  We maintain quality control through human oversight while leveraging AI to enhance efficiency and creativity in our design process.
                </p>
              </div>
            </div>
          </section>

          {/* Section 9: Privacy */}
          <section id="privacy" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4">
                <Shield className="w-6 h-6 text-teal-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">9. Privacy and Data Protection</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We are committed to protecting your privacy and personal information. Our collection and use of data is governed by our Privacy Policy, which forms part of these Terms of Service.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">We collect only necessary information to provide our services</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">Your data is stored securely and never sold to third parties</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">You have the right to request deletion of your personal data</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 10: Third-Party Services */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
                <Globe className="w-6 h-6 text-gray-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">10. Third-Party Services</h2>
            </div>
            <div className="ml-16">
              <p className="text-lg text-gray-700 leading-relaxed">
                We may use external platforms such as Netlify, Supabase, Google Cloud, Stripe, and others to process or host portions of our services. These providers have their own terms which you agree to by using our site.
              </p>
            </div>
          </section>

          {/* Section 11: Limitation of Liability */}
          <section id="liability" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-pink-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">11. Limitation of Liability</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We are not liable for any direct, indirect, incidental, or consequential damages arising from the use of our services, including but not limited to loss of data, revenue, or business opportunities.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our total liability to you for any claims arising from these terms or your use of our services shall not exceed the amount you paid for the specific service in question.
              </p>
            </div>
          </section>

          {/* Section 12: Termination */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mr-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">12. Termination</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We reserve the right to refuse service or terminate your access to the site at any time, without notice, for any reason, including breach of these terms.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Upon termination, your right to use our services ceases immediately, though provisions regarding intellectual property, liability, and indemnification shall survive.
              </p>
            </div>
          </section>

          {/* Section 13: Changes to Terms */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10">
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mr-4">
                <FileText className="w-6 h-6 text-violet-600" />
              </div>
              <h2 className="text-3xl font-semibold text-gray-900">13. Changes to Terms</h2>
            </div>
            <div className="ml-16 space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                We may update these terms from time to time. Your continued use of the site indicates your acceptance of the latest version.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We will notify users of significant changes through email or prominent website notices when possible.
              </p>
            </div>
          </section>

        </div>

        {/* Contact Footer */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 mt-16 text-center text-white">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-semibold mb-4">Questions About These Terms?</h3>
            <p className="text-blue-100 mb-8 text-lg">
              If you have any questions about these Terms of Service, please don't hesitate to contact us. We're here to help.
            </p>
            <button 
              onClick={handleContactClick}
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Mail className="w-5 h-5 mr-3" />
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TermsOfService;